/**
 * Whose turn it is on a club's approval ladder.
 *
 *   To Do -> Created -> Checked -> Reviewed -> Approved
 *             moderator  levelHead  registrar  principal
 *
 * The rungs live here rather than in the screen that draws them, because three
 * places need the same answer and must not be able to disagree: the club's own
 * approval bar, the status column on the club list, and the dashboard card that
 * tells someone there is work waiting. A club that says "Waiting on the level
 * head" in one place and counts as the registrar's in another is worse than no
 * count at all.
 *
 * This is the club half of the ladder. Who may EDIT the marks at each rung is
 * workflowStatus.canEditMarks, shared with subjects; only the naming of whose
 * move it is lives here, because a club's first rung is its moderator rather
 * than a subject teacher.
 */

export const LADDER = {
  'To Do': { next: 'Created', by: 'moderator', action: 'Submit for checking' },
  Discarded: { next: 'Created', by: 'moderator', action: 'Resubmit' },
  Resubmit: { next: 'Created', by: 'moderator', action: 'Resubmit' },
  Created: { next: 'Checked', by: 'levelHead', action: 'Check' },
  Checked: { next: 'Reviewed', by: 'registrar', action: 'Review' },
  Reviewed: { next: 'Approved', by: 'principal', action: 'Approve' },
};

/** The colour each rung wears, so one status reads the same everywhere. */
export const STATUS_COLOR = {
  Approved: 'green',
  Reviewed: 'olive',
  Checked: 'teal',
  Created: 'blue',
  Discarded: 'red',
  Resubmit: 'orange',
};

/** Who the ladder is waiting for. Null once the principal has signed. */
export function waitingOn(status) {
  const rung = LADDER[status || 'To Do'];
  return rung ? rung.by : null;
}

/**
 * Whether this club is waiting on this particular user.
 *
 * Admin stands in for every rung, as it does everywhere else here - which is
 * why an administrator's dashboard shows everything outstanding rather than
 * nothing.
 *
 * @param {string} status
 * @param {object} user  { userType, isModerator }
 */
export function awaitsUser(status, user) {
  const by = waitingOn(status);
  if (!by) return false;

  const u = user || {};
  if (u.userType === 'admin') return true;
  if (by === 'moderator') return Boolean(u.isModerator);
  return u.userType === by;
}

/** What to call the rung in a sentence. */
export function waitingLabel(status) {
  const by = waitingOn(status);
  if (!by) return 'Signed off';

  return {
    moderator: 'Waiting on the moderator',
    levelHead: 'Waiting on the level head',
    registrar: 'Waiting on the registrar',
    principal: 'Waiting on the principal',
  }[by];
}
